import { User } from '../redux/types/authTypes';

/**
 * Normalizes a status string to a standard format for permission checking
 * @param status The status string to normalize
 * @returns The normalized status string
 */
export const normalizeStatus = (status: string | null | undefined): string => {
  if (!status) return 'ASSIGNED'; // Default to ASSIGNED if no status provided

  // Convert to uppercase and trim whitespace
  const upperCaseStatus = status.toUpperCase().trim();

  // Map of different status formats to their normalized versions
  const statusMap: Record<string, string> = {
    // Standard statuses
    ASSIGNED: 'ASSIGNED',
    UNASSIGNED: 'UNASSIGNED',
    IN_PROGRESS: 'IN_PROGRESS',
    REVIEW: 'REVIEW',
    DELIVERED: 'DELIVERED',
    BILLED: 'BILLED',
    HOLD: 'HOLD',
    CANCELLED: 'CANCELLED',
    PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
    REOPEN: 'REOPEN',
    NON_ACTIVE: 'NON_ACTIVE',

    // Alternative formats
    'IN-PROGRESS': 'IN_PROGRESS',
    INPROGRESS: 'IN_PROGRESS',
    'FOR REVIEW': 'REVIEW',
    'FOR-REVIEW': 'REVIEW',
    FORREVIEW: 'REVIEW',
    'PAYMENT RECEIVED': 'PAYMENT_RECEIVED',
    PAYMENTRECEIVED: 'PAYMENT_RECEIVED',
    'ON HOLD': 'HOLD',
    'ON-HOLD': 'HOLD',
    ONHOLD: 'HOLD',
    CANCEL: 'CANCELLED',
    'NON ACTIVE': 'NON_ACTIVE',
    NONACTIVE: 'NON_ACTIVE',
  };

  // Return the mapped status or the original if not found
  return statusMap[upperCaseStatus] || upperCaseStatus;
};

/**
 * Checks if a user has a specific permission
 * @param user The user object from Redux state
 * @param permissionKey The specific permission key to check (e.g., 'projectWrite', 'customerEdit')
 * @returns boolean indicating if user has the permission
 */
export const hasPermission = (user: User | null, permissionKey: string): boolean => {
  // If user is superAdmin, they have all permissions
  if (user?.designation === 'superAdmin') {
    return true;
  }

  // If no user or no permissions, deny access
  if (!user || !user.permissions) {
    return false;
  }

  let hasSpecificPermission = false;

  // Check if permissions is an object with numeric keys (like {0: {...}})
  if (typeof user.permissions === 'object' && '0' in user.permissions) {
    //@ts-ignore
    hasSpecificPermission = Boolean(user.permissions[0][permissionKey]);
  }
  // Check if permissions is an array
  else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    hasSpecificPermission = Boolean(user.permissions[0][permissionKey]);
  }
  // Direct object access if neither of the above
  else if (typeof user.permissions === 'object') {
    hasSpecificPermission = Boolean(user.permissions[permissionKey]);
  }

  return hasSpecificPermission;
};

/**
 * Checks if a user has permission for a specific project status
 * @param user The user object from Redux state
 * @param status The project status to check permission for
 * @returns boolean indicating if user has permission for the status
 */
export const hasStatusPermission = (
  user: User | null,
  status: string | null | undefined
): boolean => {
  // If no status provided, default to requiring basic project edit permission
  if (!status) {
    console.log('hasStatusPermission: No status provided, using projectEdit permission');
    return hasPermission(user, 'projectEdit');
  }

  // If user is superAdmin, they have all permissions
  if (user?.designation === 'superAdmin') {
    console.log(`hasStatusPermission: User is superAdmin, allowing status ${status}`);
    return true;
  }

  // If no user or no permissions, deny access
  if (!user || !user.permissions) {
    console.log('hasStatusPermission: No user or no permissions, denying access');
    return false;
  }

  // Normalize the status using our helper function
  const normalizedStatus = normalizeStatus(status);

  console.log(
    `hasStatusPermission: Checking permission for status: ${status} (normalized: ${normalizedStatus})`
  );

  // Map of statuses to the permissions required to handle them
  const statusPermissionMap: Record<string, string> = {
    REVIEW: 'canReview',
    IN_PROGRESS: 'canInProgress',
    DELIVERED: 'canDeliverProject',
    BILLED: 'canBillProject',
    HOLD: 'canHoldProject',
    CANCELLED: 'canCancelProject',
    PAYMENT_RECEIVED: 'canReceivePayment',
    REOPEN: 'canReopenProject',
    // Default permissions for other statuses
    ASSIGNED: 'canAssignProject', // Fixed: Use correct permission name
    UNASSIGNED: 'projectEdit',
    NON_ACTIVE: 'projectEdit',
  };

  // Get the permission key for this status
  const permissionKey = statusPermissionMap[normalizedStatus] || 'projectEdit';

  console.log(`hasStatusPermission: Permission required for ${normalizedStatus}: ${permissionKey}`);

  // Extract user permissions for debugging
  let userPermissionsObj: Record<string, boolean> = {};

  if (typeof user.permissions === 'object' && '0' in user.permissions) {
    //@ts-ignore
    userPermissionsObj = user.permissions[0];
  } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    userPermissionsObj = user.permissions[0];
  } else if (typeof user.permissions === 'object') {
    userPermissionsObj = user.permissions as Record<string, boolean>;
  }

  console.log('hasStatusPermission: User permissions:', userPermissionsObj);
  console.log(
    `hasStatusPermission: User has ${permissionKey}:`,
    Boolean(userPermissionsObj[permissionKey])
  );

  // Check if the user has the required permission
  const hasRequiredPermission = hasPermission(user, permissionKey);

  console.log(
    `hasStatusPermission: Final result for ${normalizedStatus}: ${hasRequiredPermission}`
  );

  return hasRequiredPermission;
};

// Project permission checks
export const hasProjectReadPermission = (user: User | null): boolean => {
  return hasPermission(user, 'projectRead');
};

export const hasProjectWritePermission = (user: User | null): boolean => {
  return hasPermission(user, 'projectWrite');
};

export const hasProjectEditPermission = (user: User | null): boolean => {
  return hasPermission(user, 'projectEdit');
};

export const hasAnyProjectPermission = (user: User | null): boolean => {
  return (
    hasProjectReadPermission(user) ||
    hasProjectWritePermission(user) ||
    hasProjectEditPermission(user)
  );
};

// Customer permission checks
export const hasCustomerReadPermission = (user: User | null): boolean => {
  return hasPermission(user, 'customerRead');
};

export const hasCustomerWritePermission = (user: User | null): boolean => {
  return hasPermission(user, 'customerWrite');
};

export const hasCustomerEditPermission = (user: User | null): boolean => {
  return hasPermission(user, 'customerEdit');
};

export const hasAnyCustomerPermission = (user: User | null): boolean => {
  return (
    hasCustomerReadPermission(user) ||
    hasCustomerWritePermission(user) ||
    hasCustomerEditPermission(user)
  );
};

// Employee permission checks
export const hasEmployeeReadPermission = (user: User | null): boolean => {
  return hasPermission(user, 'employeeRead');
};

export const hasEmployeeWritePermission = (user: User | null): boolean => {
  return hasPermission(user, 'employeeWrite');
};

export const hasEmployeeEditPermission = (user: User | null): boolean => {
  return hasPermission(user, 'employeeEdit');
};

export const hasAnyEmployeePermission = (user: User | null): boolean => {
  return (
    hasEmployeeReadPermission(user) ||
    hasEmployeeWritePermission(user) ||
    hasEmployeeEditPermission(user)
  );
};

export const hasManageEmployeePermission = (user: User | null): boolean => {
  return hasPermission(user, 'canManageEmployee');
};

// Role permission checks
export const hasRoleReadPermission = (user: User | null): boolean => {
  return hasPermission(user, 'roleRead');
};

export const hasRoleWritePermission = (user: User | null): boolean => {
  return hasPermission(user, 'roleWrite');
};

export const hasRoleEditPermission = (user: User | null): boolean => {
  return hasPermission(user, 'roleEdit');
};

export const hasAnyRolePermission = (user: User | null): boolean => {
  return hasRoleReadPermission(user) || hasRoleWritePermission(user) || hasRoleEditPermission(user);
};

// Finance permission check
export const hasFinancePermission = (user: User | null): boolean => {
  // If user is superAdmin, they have all permissions
  if (user?.designation === 'superAdmin') {
    return true;
  }

  // Check for financeAccess permission
  return hasPermission(user, 'financeAccess');
};

// Project-specific permission checks for editing details
export const hasChangeProjectNamePermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeProjectName');
};

export const hasChangeArticleNumberPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeArticleNumber');
};

export const hasChangeCompanyNamePermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeCompanyName');
};

export const hasTransferProjectPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canTransferProject');
};

export const hasChangeProjectDescriptionPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeProjectDescription');
};

export const hasChangeProjectRemarksPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeProjectRemarks');
};

export const hasChangeProjectCostPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeProjectCost');
};

export const hasUploadProductImagePermission = (user: User | null): boolean => {
  return hasPermission(user, 'canUploadProductImage');
};

export const hasChangeBillNumberPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeBillNumber');
};

export const hasChangeWarrantyPeriodPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeWarrantyPeriod');
};

export const hasChangeRevisionAmountPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeRevisionAmount');
};

export const hasChangeRevisionReasonPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeRevisionReason');
};

export const hasChangeRevisionChargeablePermission = (user: User | null): boolean => {
  return hasPermission(user, 'canChangeRevisionChargeable');
};

export const hasBillProjectPermission = (user: User | null): boolean => {
  return hasPermission(user, 'canBillProject');
};
